
import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight } from 'lucide-react';
import FormSections from './components/FormSections';
import FormProgressIndicator from './FormProgressIndicator';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBusinessFormContext } from '@/context/BusinessFormContext';

interface FormContentProps {
  handleSubmit: () => void; // Main form submission handler
  onBackButtonClick?: () => void; // Optional back button handler
  isSubmitting: boolean;      // To disable button during submission
  formError: string | null;   // To display submission errors
  // Other props specific to FormContent's direct responsibilities, if any.
}

const FormContent: React.FC<FormContentProps> = ({
  handleSubmit,
  isSubmitting,
  formError,
  // onBackButtonClick // Not used in this snippet, but kept in props if needed elsewhere
}) => {
  const {
    isSubmitEnabled,
    basicInfoEdited,
    addressEdited,
    industryEdited,
    userDetailsComplete, // userDetailsComplete is used for progress
  } = useBusinessFormContext();

  // Calculate progress percentage based on EDITED sections (or completed for userDetails)
  const totalSteps = 4;
  const completedSteps = [
    basicInfoEdited,
    addressEdited,
    industryEdited,
    userDetailsComplete, // UserDetails section is considered "done" when its fields are valid
  ].filter(Boolean).length;
  
  const progressPercentage = (completedSteps / totalSteps) * 100;
  const isMobile = useIsMobile();
  
  return (
    <div className="space-y-6">      
      <div className="space-y-6">
        <FormSections
          // No props are passed to FormSections as it now uses context
        />
      </div>
      
      {/* Remove margin-top and padding-top for mobile */}
      <div className={`${isMobile ? '' : 'mt-20 pt-10'}`}>
        {formError && (
          <p className="text-sm text-red-600 mb-2 text-center">{formError}</p>
        )}
        <Button 
          onClick={handleSubmit}
          disabled={!isSubmitEnabled || isSubmitting}
          className="w-full py-6 text-lg"
        >
          {isSubmitting ? 'Submitting...' : <>Continue to Onboarding <ArrowRight className="ml-2" /></>}
        </Button>
        
        {!isSubmitEnabled && !formError && ( // Only show this if no other error is present
          <p className="text-sm text-gray-500 mt-2 mb-12 text-center">
            Complete all required information to enable this button
          </p>
        )}
      </div>

      {/* Mobile sticky progress bar at bottom */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 bg-white shadow-[0_-2px_5px_rgba(0,0,0,0.1)] p-2 z-50">
          <FormProgressIndicator
            // Props are removed as FormProgressIndicator now uses context.
            // It still accepts onBackButtonClick and showBackButton if needed elsewhere.
            showBackButton={false} // This was the only prop explicitly set here.
          />
        </div>
      )}
    </div>
  );
};

export default FormContent;
